import { Eye, RefreshCw } from "lucide-react";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../components/PageTitle";
import {
  type DeliveryItem,
  useCancelDeliveryApi,
  useDeleteDeliveryApi,
  useDeliveriesApi,
} from "../../hooks/use-delivery-api";

type TabStatus = "draft" | "scheduled" | "history";

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  sent: { label: "送信完了", cls: "badge-success" },
  // biome-ignore lint/style/useNamingConvention: matches backend DeliveryStatus enum value
  partially_sent: { label: "一部失敗", cls: "badge-warning" },
  failed: { label: "失敗", cls: "badge-error" },
  sending: { label: "送信中", cls: "badge-info" },
  scheduled: { label: "予約中", cls: "badge-accent" },
  draft: { label: "下書き", cls: "badge-ghost" },
  cancelled: { label: "キャンセル", cls: "badge-neutral" },
};

export const DeliveriesPage: FC = () => {
  const [tab, setTab] = useState<TabStatus>("draft");
  const [page, setPage] = useState(1);

  const { data, loading, error, refetch } = useDeliveriesApi({
    status: tab,
    page,
    limit: 20,
  });
  const { deleteDelivery } = useDeleteDeliveryApi();
  const { cancelDelivery } = useCancelDeliveryApi();

  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "cancel";
    id: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleTabChange = (newTab: TabStatus) => {
    setTab(newTab);
    setPage(1);
  };

  const handleAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      if (confirmAction.type === "delete") {
        await deleteDelivery(confirmAction.id);
      } else {
        await cancelDelivery(confirmAction.id);
      }
      setConfirmAction(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 1;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>一斉配信</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>一斉配信</PageTitle>
        <Link to="/deliveries/new" className="btn btn-primary btn-sm">
          + 新規配信
        </Link>
      </div>

      {/* タブ */}
      <div className="tabs tabs-boxed mb-6 bg-base-200">
        <button
          type="button"
          className={`tab ${tab === "draft" ? "tab-active" : ""}`}
          onClick={() => handleTabChange("draft")}
        >
          下書き
        </button>
        <button
          type="button"
          className={`tab ${tab === "scheduled" ? "tab-active" : ""}`}
          onClick={() => handleTabChange("scheduled")}
        >
          予約中
        </button>
        <button
          type="button"
          className={`tab ${tab === "history" ? "tab-active" : ""}`}
          onClick={() => handleTabChange("history")}
        >
          配信履歴
        </button>
      </div>

      {/* テーブル */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>件名</th>
                <th>配信先グループ</th>
                <th>送信者</th>
                {tab === "history" && <th>宛先数</th>}
                {tab === "scheduled" && <th>予約日時</th>}
                {tab === "history" && <th>配信日時</th>}
                {tab === "history" && (
                  <th className="whitespace-nowrap">ステータス</th>
                )}
                {tab === "draft" && <th>作成日時</th>}
                {tab === "scheduled" && <th>作成日時</th>}
                <th className="pr-6">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={tab === "history" ? 7 : 5}
                    className="text-center py-8"
                  >
                    <span className="loading loading-spinner loading-md" />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={tab === "history" ? 7 : 5}
                    className="text-center py-8 text-base-content/60"
                  >
                    {tab === "draft" && "下書きがありません"}
                    {tab === "scheduled" && "予約中の配信がありません"}
                    {tab === "history" && "配信履歴がありません"}
                  </td>
                </tr>
              ) : (
                data?.items.map((item: DeliveryItem) => (
                  <tr key={item.id} className="hover">
                    <td>
                      <Link
                        to={
                          tab === "draft"
                            ? `/deliveries/${item.id}/edit`
                            : `/deliveries/${item.id}`
                        }
                        className="link link-primary font-medium"
                      >
                        {item.subject}
                      </Link>
                    </td>
                    <td>{item.groupName || "-"}</td>
                    <td>{item.fromName || item.fromEmail}</td>
                    {tab === "history" && (
                      <td>
                        <span className="text-success">
                          {item.successCount}
                        </span>
                        /<span className="text-error">{item.failureCount}</span>
                        <span className="text-base-content/60">
                          {" "}
                          ({item.totalRecipients})
                        </span>
                      </td>
                    )}
                    {tab === "scheduled" && (
                      <td>{formatDate(item.scheduledAt)}</td>
                    )}
                    {tab === "history" && <td>{formatDate(item.sentAt)}</td>}
                    {tab === "history" && (
                      <td>
                        <span
                          className={`badge badge-sm whitespace-nowrap ${STATUS_BADGE[item.status]?.cls || "badge-ghost"}`}
                        >
                          {STATUS_BADGE[item.status]?.label || item.status}
                        </span>
                      </td>
                    )}
                    {(tab === "draft" || tab === "scheduled") && (
                      <td>{formatDate(item.createdAt)}</td>
                    )}
                    <td className="pr-6">
                      <div className="flex gap-2">
                        {tab === "draft" && (
                          <>
                            <Link
                              to={`/deliveries/${item.id}/edit`}
                              className="btn btn-ghost btn-xs"
                            >
                              編集
                            </Link>
                            <button
                              type="button"
                              onClick={() =>
                                setConfirmAction({
                                  type: "delete",
                                  id: item.id,
                                })
                              }
                              className="btn btn-ghost btn-xs text-error"
                            >
                              削除
                            </button>
                          </>
                        )}
                        {tab === "scheduled" && (
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmAction({
                                type: "cancel",
                                id: item.id,
                              })
                            }
                            className="btn btn-ghost btn-xs text-warning"
                          >
                            キャンセル
                          </button>
                        )}
                        {tab === "history" && (
                          <>
                            <Link
                              to={`/deliveries/${item.id}`}
                              className="btn btn-outline btn-xs gap-1"
                            >
                              <Eye size={14} />
                              詳細
                            </Link>
                            <Link
                              to={`/deliveries/new?copy-from=${item.id}`}
                              className="btn btn-outline btn-primary btn-xs gap-1"
                            >
                              <RefreshCw size={14} />
                              再配信
                            </Link>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              &laquo;
            </button>
            <button type="button" className="join-item btn btn-sm">
              {page} / {totalPages}
            </button>
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              &raquo;
            </button>
          </div>
        </div>
      )}

      {/* 確認モーダル */}
      {confirmAction && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {confirmAction.type === "delete"
                ? "配信を削除しますか？"
                : "予約をキャンセルしますか？"}
            </h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                戻る
              </button>
              <button
                type="button"
                className={
                  confirmAction.type === "delete"
                    ? "btn btn-error"
                    : "btn btn-warning"
                }
                onClick={handleAction}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : confirmAction.type === "delete" ? (
                  "削除"
                ) : (
                  "キャンセル"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setConfirmAction(null)}
          />
        </div>
      )}
    </div>
  );
};
